import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CardHeader,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
  StatTile,
  TrendChart,
  cn,
  type DataTableCard,
} from '@bcecloud/portal-ui';
import { CalendarClockIcon } from 'lucide-react';
import { errorMessage } from '@/lib/api';
import {
  formatMoney,
  formatMoneyCompact,
  shortDate,
  accuracyRows,
  meanAbsolute,
  useForecastAccuracy,
  ymd,
} from '@/lib/cash-flow';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';

/** The two horizons the API compares (`?horizon=`). */
const HORIZON = {
  one: '1',
  four: '4',
} as const;
type Horizon = (typeof HORIZON)[keyof typeof HORIZON];
const HORIZON_PARAM = 'horizon';
const HORIZON_OPTIONS = [
  { key: HORIZON.one, label: 'First week' },
  { key: HORIZON.four, label: 'First 4 weeks', shortLabel: '4 weeks' },
] as const;

const ACCURACY_SORT_FIELDS = {
  week: 'weekStart',
  inVariance: 'inVariance',
  outVariance: 'outVariance',
  closingVariance: 'closingVariance',
} as const;
const ACCURACY_SORT_KEYS = Object.values(ACCURACY_SORT_FIELDS);
const DEFAULT_SORT = { key: ACCURACY_SORT_FIELDS.week, direction: 'desc' } as const;

function signed(value: number): string {
  return `${value > 0 ? '+' : ''}${formatMoney(value)}`;
}

/** A forecast / actual / error triple in one cell. */
function Compare({
  forecast,
  actual,
  variance,
  downIsGood = false,
}: {
  readonly forecast: number;
  readonly actual: number | null;
  /** actual − forecast. */
  readonly variance: number | null;
  /** Cash out: spending less than forecast is the good direction. */
  readonly downIsGood?: boolean;
}): React.ReactElement {
  const good = variance === null ? null : downIsGood ? variance <= 0 : variance >= 0;
  return (
    <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">
      <span className="block text-portal-ink">
        {actual === null ? '—' : formatMoney(actual)}
        <span className="text-portal-muted"> vs {formatMoney(forecast)}</span>
      </span>
      <span
        className={cn(
          'block text-portal-micro',
          good === null ? 'text-portal-muted' : good ? 'text-portal-success' : 'text-rose-700',
        )}
      >
        {variance === null ? 'Not known yet' : `${signed(variance)} error`}
      </span>
    </td>
  );
}

/**
 * Analytics → Accounting → Forecast vs actual: each Monday snapshot's
 * forecast against what the books say happened, for its first week and its
 * first four, with the error. Empty until the first snapshot is frozen.
 * Behind view_accounting.
 */
export function ForecastAccuracyPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const horizon: Horizon = params.get(HORIZON_PARAM) === HORIZON.four ? HORIZON.four : HORIZON.one;
  const { sort, onSort } = useTableSort(ACCURACY_SORT_KEYS, DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch();
  const accuracy = useForecastAccuracy();

  const allRows = useMemo(() => accuracyRows(accuracy.data?.rows ?? [], Number(horizon)), [accuracy.data, horizon]);
  const rows = useMemo(() => sortRows(filterRows(allRows, search, ['weekText']), sort), [allRows, search, sort]);
  const finished = allRows.filter((row) => row.complete);
  const chartRows = [...finished].sort((a, b) => a.weekStart.localeCompare(b.weekStart));

  function setHorizon(next: Horizon): void {
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        if (next === HORIZON.one) nextParams.delete(HORIZON_PARAM);
        else nextParams.set(HORIZON_PARAM, next);
        return nextParams;
      },
      { replace: true },
    );
  }

  const cards: DataTableCard[] = rows.map((row) => ({
    key: row.snapshotId,
    title: row.weekText,
    subtitle: row.complete ? 'Finished' : 'Still running',
    fields: [
      { label: 'Cash in error', value: signed(row.inVariance) },
      { label: 'Cash out error', value: signed(row.outVariance) },
      { label: 'Closing error', value: row.closingVariance === null ? 'Not known yet' : signed(row.closingVariance) },
    ],
  }));

  const noSnapshots = accuracy.isSuccess && accuracy.data.rows.length === 0;
  const averageText = (value: number | null): string => (value === null ? 'None yet' : formatMoney(value));

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ACCOUNTING"
        title="Forecast vs actual"
        subtitle="Each Monday's forecast against what actually moved through the bank accounts, so the forecast earns trust or gets fixed."
      />

      {accuracy.isError ? (
        <ErrorNotice title="Forecast accuracy unavailable" message={errorMessage(accuracy.error)} />
      ) : accuracy.isPending ? (
        <SkeletonTable label="Loading snapshots…" />
      ) : noSnapshots ? (
        <Panel>
          <EmptyState
            icon={<CalendarClockIcon aria-hidden className="size-5" />}
            title="No snapshots yet"
            message="The first snapshot is taken Monday at 6am. A week later this page compares it with what actually happened."
          />
        </Panel>
      ) : (
        <>
          <div className="mb-4">
            <SegmentedControl<Horizon> label="Horizon" value={horizon} onChange={setHorizon} options={HORIZON_OPTIONS} />
          </div>

          <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatTile label="Snapshots" value={String(allRows.length)} hint={`${finished.length} finished`} />
            <StatTile label="Avg cash in error" value={averageText(meanAbsolute(finished.map((row) => row.inVariance)))} hint="Either direction" />
            <StatTile label="Avg cash out error" value={averageText(meanAbsolute(finished.map((row) => row.outVariance)))} hint="Either direction" />
            <StatTile
              label="Avg closing error"
              value={averageText(meanAbsolute(finished.map((row) => row.closingVariance)))}
              hint="Against the next Monday's balance"
            />
          </div>

          {chartRows.length > 1 ? (
            <Panel className="mb-4 p-5">
              <CardHeader
                title="Closing cash: forecast vs actual"
                description="What each snapshot said cash would close at, against the balance entered the Monday after."
              />
              <TrendChart
                title="Forecast and actual closing cash by snapshot week"
                unit="Forecast closing"
                variant="line"
                points={chartRows.map((row) => ({ key: ymd(row.weekStart), label: shortDate(row.weekStart), value: row.forecastClosing }))}
                series={[
                  { key: 'forecast', label: 'Forecast closing', values: chartRows.map((row) => row.forecastClosing) },
                  { key: 'actual', label: 'Actual closing', values: chartRows.map((row) => row.actualClosing ?? Number.NaN) },
                ]}
                formatValue={formatMoneyCompact}
                height={260}
              />
            </Panel>
          ) : null}

          <div className="mb-4 flex flex-wrap items-center gap-3">
            <SearchInput
              label="Search snapshots"
              placeholder="Week, e.g. Oct"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full max-w-sm"
            />
          </div>

          {rows.length === 0 ? (
            <Panel>
              {search.trim() !== '' ? (
                <EmptyState title="No snapshots match" message="Try a different search." />
              ) : (
                <EmptyState
                  title="Not enough weeks yet"
                  message="No snapshot is old enough for this horizon. Switch to the first week, or check back in a few Mondays."
                />
              )}
            </Panel>
          ) : (
            <DataTable
              headers={[
                { label: 'Snapshot', sortKey: ACCURACY_SORT_FIELDS.week },
                { label: 'Cash in: actual vs forecast', sortKey: ACCURACY_SORT_FIELDS.inVariance },
                { label: 'Cash out: actual vs forecast', sortKey: ACCURACY_SORT_FIELDS.outVariance },
                { label: 'Closing: actual vs forecast', sortKey: ACCURACY_SORT_FIELDS.closingVariance },
                'Status',
              ]}
              rightAlignedHeaders={[1, 2, 3]}
              sort={sort}
              onSort={onSort}
              minWidth={900}
              cards={cards}
            >
              {rows.map((row) => (
                <tr key={row.snapshotId} className="border-t border-portal-line align-middle">
                  <td className="whitespace-nowrap p-3 text-portal-small font-bold text-portal-ink">{row.weekText}</td>
                  <Compare forecast={row.forecastIn} actual={row.actualIn} variance={row.inVariance} />
                  <Compare forecast={row.forecastOut} actual={row.actualOut} variance={row.outVariance} downIsGood />
                  <Compare forecast={row.forecastClosing} actual={row.actualClosing} variance={row.closingVariance} />
                  <td className="p-3 text-portal-small">
                    {row.complete ? <Pill tone="success">Finished</Pill> : <Pill tone="neutral">Still running</Pill>}
                  </td>
                </tr>
              ))}
            </DataTable>
          )}
        </>
      )}
    </section>
  );
}
